import { z } from 'zod';

/**
 * The user-facing list of style presets. Each preset routes to a default
 * image model and seeds the amplifier with style-specific guidance.
 *
 * The order here is the order shown in the UI.
 */
export const STYLE_PRESETS = [
  'photoreal-cinematic',
  'editorial-illustration',
  'anime-leaning',
  '3d-render',
  'painterly',
  'logo-poster',
  'experimental',
] as const;

export const StylePresetSchema = z.enum(STYLE_PRESETS);
export type StylePreset = z.infer<typeof StylePresetSchema>;

export interface StyleProfile {
  id: StylePreset;
  label: string;
  oneLiner: string;
  /** Default image model when this preset is picked. */
  defaultModel: ImageModelId;
  /** Hint appended to the amplifier system prompt. */
  amplifierHint: string;
}

export const STYLE_PROFILES: Record<StylePreset, StyleProfile> = {
  'photoreal-cinematic': {
    id: 'photoreal-cinematic',
    label: 'Photoreal cinematic',
    oneLiner: 'Like a still from a film. Real cameras, real lights.',
    defaultModel: 'flux-1.1-pro',
    amplifierHint:
      'Lean photographic: name the lens (35mm / 85mm), aperture, lighting source (key/fill/rim), film stock or digital body when it helps. Composition language (rule of thirds, leading line, depth of field).',
  },
  'editorial-illustration': {
    id: 'editorial-illustration',
    label: 'Editorial illustration',
    oneLiner: 'Magazine-cover energy. Bold color, confident gesture.',
    defaultModel: 'gpt-image-1.5',
    amplifierHint:
      'Lean illustrative: shape language, palette callouts, line weight, paper-stock connotation. Reference editorial-illustrator vocabulary without naming living artists.',
  },
  'anime-leaning': {
    id: 'anime-leaning',
    label: 'Anime-leaning',
    oneLiner: 'Inked linework, cel shading, expressive eyes.',
    defaultModel: 'flux-1.1-pro',
    amplifierHint:
      'Lean anime/manga: cel shading, ink linework weight, gesture, screen-tone callouts. Avoid hyper-realism descriptors.',
  },
  '3d-render': {
    id: '3d-render',
    label: '3D render',
    oneLiner: 'Studio render. Subsurface, soft GI, refined materials.',
    defaultModel: 'flux-1.1-pro',
    amplifierHint:
      'Lean CG: material BRDFs (subsurface, anisotropic, microfacet), studio HDRI, global illumination, polygon density implied via subdivision.',
  },
  painterly: {
    id: 'painterly',
    label: 'Painterly',
    oneLiner: 'Visible brushwork. Oil, gouache, or watercolor.',
    defaultModel: 'gpt-image-1.5',
    amplifierHint:
      'Lean painterly: brushwork direction, impasto / wash / glaze, ground color, edge softness. Name medium (oil, gouache, watercolor). Avoid "8K" or "photorealistic".',
  },
  'logo-poster': {
    id: 'logo-poster',
    label: 'Logo / poster (with text)',
    oneLiner: 'Type-friendly. Strong silhouettes. Read-from-across-a-room.',
    defaultModel: 'gpt-image-1.5',
    amplifierHint:
      'Lean graphic: clear silhouette, strong negative space, type-friendly layout. If text is in the brief, specify exact copy + font feel + placement.',
  },
  experimental: {
    id: 'experimental',
    label: 'Experimental / vibes',
    oneLiner: 'Loose. Surprise yourself.',
    defaultModel: 'grok-image',
    amplifierHint:
      'Embrace ambiguity. Sensory language over technical. Allow surprising juxtapositions and unexpected color.',
  },
};

// --- Image models --------------------------------------------------------

export const IMAGE_MODELS = ['flux-1.1-pro', 'gpt-image-1.5', 'grok-image'] as const;
export const ImageModelSchema = z.enum(IMAGE_MODELS);
export type ImageModelId = z.infer<typeof ImageModelSchema>;

export interface ImageModelProfile {
  id: ImageModelId;
  label: string;
  costUsdPerImage: number;
  /** Provider key (which adapter to call). */
  provider: 'fal' | 'openai' | 'xai';
  /** Display tier (used for badges in UI). */
  tier: 'standard' | 'premium' | 'experimental';
}

export const IMAGE_MODEL_PROFILES: Record<ImageModelId, ImageModelProfile> = {
  'flux-1.1-pro': {
    id: 'flux-1.1-pro',
    label: 'FLUX 1.1 [pro]',
    costUsdPerImage: 0.04,
    provider: 'fal',
    tier: 'standard',
  },
  'gpt-image-1.5': {
    id: 'gpt-image-1.5',
    label: 'GPT Image 1.5',
    costUsdPerImage: 0.07,
    provider: 'openai',
    tier: 'premium',
  },
  'grok-image': {
    id: 'grok-image',
    label: 'Grok image',
    costUsdPerImage: 0.02,
    provider: 'xai',
    tier: 'experimental',
  },
};
